import * as requests from '../api-request'
import { createQueryString } from '../api-request'
import getToken from '../get-token'

export const getStoryboardData = async (data = {}, token) => {
  token = token || (await getToken())
  if (!token) {
    throw Error('Token is required')
  }
  const queryString = createQueryString(data)
  return requests.toJSON('GET', `storyboard/get_storyboard_data?${queryString}`, {}, token)
}

export const getStoryboard = async (data = {}, token) => {
  token = token || (await getToken())

  const queryString = createQueryString(data)
  return requests.toJSON('GET', `storyboard/get_storyboard?${queryString}`, {}, token)
}

export const createNewStoryboard = async (data, token) => {
  token = token || (await getToken())
  if (!token) {
    throw Error('Token is required')
  }
  return requests.toJSON('POST', 'storyboard/create', data, token)
}

export const getFrame = async (data, token) => {
  token = token || (await getToken())
  if (!token) {
    throw Error('Token is required')
  }
  const queryString = createQueryString(data)
  return requests.toJSON('GET', `storyboard/get_scene_frames?${queryString}`, {}, token)
}

export const getLatestChanges = async (data, token) => {
  const queryString = createQueryString(data)
  return requests.toJSON('GET', `storyboard/get_latest_changes?${queryString}`, {}, token)
}

export const getAllFrames = async (data, token) => {
  token = token || (await getToken())
  if (!token) {
    throw Error('Token is required')
  }
  return requests.toJSON('POST', 'storyboard/get_scene_frames', data, token)
}

export const updateStoryboardSnapshot = async (data) => {
  return requests.toJSON('POST', 'storyboard/update_frame', data)
}

export const drawScene = async (data = {}, token) => {
  token = token || (await getToken())
  if (!token) {
    throw Error('Token is required')
  }
  return requests.toJSON('POST', 'storyboard/draw_scene', data, token)
}

export const createStoryboardJob = async (data, token) => {
  token = token || (await getToken())
  return requests.toJSON('POST', 'storyboard/automatic_storyboard', data, token)
}

export const createStoryboardExportJob = async (data, token) => {
  token = token || (await getToken())
  return requests.toJSON('POST', 'storyboard/export_pdf', data, token)
}

export const getRunningPdfExport = async (data, token) => {
  token = token || (await getToken())
  const queryString = createQueryString(data)
  return requests.toJSON('GET', `storyboard/running_pdf_export?${queryString}`, token)
}

export const getCompletedPdfs = async (data, token) => {
  token = token || (await getToken())
  const queryString = createQueryString(data)
  return requests.toJSON('GET', `storyboard/completed_pdfs?${queryString}`, token)
}

export const cancelStoryboardJob = async (data, token) => {
  token = token || (await getToken())
  return requests.toJSON('POST', 'storyboard/cancel_automatic_storyboard', data, token)
}

export const getJobStatus = async (data, token) => {
  token = token || (await getToken())
  const queryString = createQueryString(data)
  return requests.toJSON('GET', `storyboard/automatic_storyboard?${queryString}`, data, token)
}

export const getSketchSettings = async (data, token) => {
  token = token || (await getToken())
  const queryString = createQueryString(data)
  return requests.toJSON('GET', `storyboard/sketch_settings?${queryString}`, {}, token)
}

export const getStoryboardFrames = async (data, token) => {
  token = token || (await getToken())
  const queryString = createQueryString(data)
  return requests.toJSON('GET', `storyboard/get_all_storyboard?${queryString}`, {}, token)
}

export const getSceneUuidsWithFrames = async (data, token) => {
  token = token || (await getToken())
  if (!token) {
    throw Error('Token is required')
  }
  return requests.toJSON('POST', 'storyboard/get_scene_uuids_with_frames', data, token)
}

export const sketchSceneFrames = async (data, token, onProgress) => {
  token = token || (await getToken())
  const response = await requests.headless('POST', 'storyboard/sketch_scene_frames', data, token)
  let finalData = null
  await requests.withStream(response, 'json', (err, msg) => {
    if (err) {
      console.warn('Stream error:', err)
      return
    }
    if (msg && !msg.streaming && msg.data) {
      finalData = msg.data
    } else if (msg && !msg.streaming && msg.error) {
      throw new Error(msg.error.message || 'Sketch scene frames failed')
    } else if (msg && msg.streaming && onProgress) {
      onProgress(msg.message?.text || 'Processing...')
    }
  })
  return finalData
}

export const calculateCredits = async (data, token) => {
  token = token || (await getToken())
  return requests.toJSON('POST', 'storyboard/calculate_credits', data, token)
}

// ==================== SHOT API METHODS ====================

export const createShot = async (data, token) => {
  token = token || (await getToken())
  return requests.toJSON('POST', 'storyboard/shot/create', data, token)
}

export const getShots = async (sceneUuid, scriptUuid, projectUuid, token) => {
  token = token || (await getToken())
  const queryString = createQueryString({ scriptUuid, projectUuid })
  return requests.toJSON('GET', `storyboard/shot/${sceneUuid}/list?${queryString}`, {}, token)
}

export const getShot = async (shotUuid, token) => {
  token = token || (await getToken())
  return requests.toJSON('GET', `storyboard/shot/${shotUuid}`, {}, token)
}

export const updateShot = async (shotUuid, data, token) => {
  token = token || (await getToken())
  return requests.toJSON('PUT', `storyboard/shot/${shotUuid}`, data, token)
}

export const deleteShot = async (shotUuid, data = {}, token) => {
  token = token || (await getToken())
  const { scriptUuid, projectUuid } = data
  const queryString = createQueryString({ scriptUuid, projectUuid })
  return requests.toJSON('DELETE', `storyboard/shot/${shotUuid}?${queryString}`, {}, token)
}

export const reorderShots = async (data, token) => {
  token = token || (await getToken())
  return requests.toJSON('POST', 'storyboard/shot/reorder', data, token)
}

export const breakdownScene = async (data, token) => {
  token = token || (await getToken())
  return requests.toJSON('POST', 'storyboard/scene/breakdown', data, token)
}

export const generateLocationSketch = async (data, token) => {
  token = token || (await getToken())
  const { sceneUuid, scriptUuid, projectUuid, ...body } = data
  const queryString = createQueryString({ scriptUuid, projectUuid })
  return requests.toJSON('POST', `storyboard/scene/${sceneUuid}/location-sketch?${queryString}`, body, token)
}

export const deleteLocationSketch = async (sceneUuid, scriptUuid, projectUuid, token) => {
  token = token || (await getToken())
  const queryString = createQueryString({ scriptUuid, projectUuid })
  return requests.toJSON('DELETE', `storyboard/scene/${sceneUuid}/location-sketch?${queryString}`, {}, token)
}

export const getScene = async (sceneUuid, scriptUuid, projectUuid, token) => {
  token = token || (await getToken())
  const queryString = createQueryString({ scriptUuid, projectUuid })
  return requests.toJSON('GET', `storyboard/scene/${sceneUuid}?${queryString}`, {}, token)
}

export const generateShotSketch = async (data, token) => {
  token = token || (await getToken())
  return requests.toJSON('POST', 'storyboard/shot/generate_sketch', data, token)
}

export const generateShotCinematic = async (data, token) => {
  token = token || (await getToken())
  return requests.toJSON('POST', 'storyboard/shot/generate_cinematic', data, token)
}

export const convertSketchToCinematic = async (data, token) => {
  // data should include: shotUuid, aspectRatio (optional), resolution (optional)
  token = token || (await getToken())
  return requests.toJSON('POST', 'storyboard/shot/convert_sketch_to_cinematic', data, token)
}

export const editShotImage = async (data, token) => {
  // data should include: shotUuid, imageType ('sketch' or 'cinematic'), editPrompt, aspectRatio (optional), resolution (optional)
  token = token || (await getToken())
  return requests.toJSON('POST', 'storyboard/shot/edit_image', data, token)
}

export const generateShotVideo = async (data, token) => {
  // data should include: shotUuid, imageType ('sketch' or 'cinematic'), videoModel, duration
  token = token || (await getToken())
  return requests.toJSON('POST', 'storyboard/shot/generate_video', data, token)
}

export const generateShotLocationReference = async (shotUuid, data, token) => {
  token = token || (await getToken())
  const { scriptUuid, projectUuid, ...body } = data
  const queryString = createQueryString({ scriptUuid, projectUuid })
  return requests.toJSON('POST', `storyboard/shot/${shotUuid}/location-reference?${queryString}`, body, token)
}

export const deleteShotLocationReference = async (shotUuid, scriptUuid, projectUuid, token) => {
  token = token || (await getToken())
  const queryString = createQueryString({ scriptUuid, projectUuid })
  return requests.toJSON('DELETE', `storyboard/shot/${shotUuid}/location-reference?${queryString}`, {}, token)
}

// ==================== SCENE LOCATION LIBRARY ====================

export const generateSceneLocation = async (sceneUuid, data, token) => {
  token = token || (await getToken())
  const { scriptUuid, projectUuid, ...body } = data
  const queryString = createQueryString({ scriptUuid, projectUuid })
  return requests.toJSON('POST', `storyboard/scene/${sceneUuid}/locations?${queryString}`, body, token)
}

export const deleteSceneLocation = async (sceneUuid, locationId, data = {}, token) => {
  token = token || (await getToken())
  const { scriptUuid, projectUuid } = data
  const queryString = createQueryString({ scriptUuid, projectUuid })
  return requests.toJSON('DELETE', `storyboard/scene/${sceneUuid}/locations/${locationId}?${queryString}`, {}, token)
}

export const linkShotLocation = async (shotUuid, locationId, data = {}, token) => {
  token = token || (await getToken())
  const { scriptUuid, projectUuid } = data
  const queryString = createQueryString({ scriptUuid, projectUuid })
  return requests.toJSON('PUT', `storyboard/shot/${shotUuid}/link-location?${queryString}`, { locationId }, token)
}

export const unlinkShotLocation = async (shotUuid, data = {}, token) => {
  token = token || (await getToken())
  const { scriptUuid, projectUuid } = data
  const queryString = createQueryString({ scriptUuid, projectUuid })
  return requests.toJSON('DELETE', `storyboard/shot/${shotUuid}/link-location?${queryString}`, {}, token)
}

export const getSimilarSceneLocations = async (sceneUuid, scriptUuid, projectUuid, token) => {
  token = token || (await getToken())
  const queryString = createQueryString({ scriptUuid, projectUuid })
  return requests.toJSON('GET', `storyboard/scene/${sceneUuid}/similar-locations?${queryString}`, {}, token)
}

export const importSceneLocation = async (sceneUuid, data, token) => {
  token = token || (await getToken())
  const { scriptUuid, projectUuid, ...body } = data
  const queryString = createQueryString({ scriptUuid, projectUuid })
  return requests.toJSON('POST', `storyboard/scene/${sceneUuid}/locations/import?${queryString}`, body, token)
}

export const uploadSceneLocation = async (sceneUuid, { file, scriptUuid, projectUuid, label }, token) => {
  token = token || (await getToken())
  const queryString = createQueryString({ scriptUuid, projectUuid })
  // eslint-disable-next-line no-undef
  const formData = new FormData()
  formData.append('file', file)
  if (label) formData.append('label', label)
  return requests.toJSON('POST', `storyboard/scene/${sceneUuid}/locations/upload?${queryString}`, formData, token)
}

// ==================== DIRECTOR'S VISION ====================

export const getDirectorsVision = async (scriptUuid, projectUuid, token) => {
  token = token || (await getToken())
  const queryString = createQueryString({ scriptUuid, projectUuid })
  return requests.toJSON('GET', `storyboard/directors-vision?${queryString}`, {}, token)
}

export const saveDirectorsVision = async (data, token) => {
  token = token || (await getToken())
  return requests.toJSON('PUT', 'storyboard/directors-vision', data, token)
}

export const deleteDirectorsVision = async (scriptUuid, projectUuid, token) => {
  token = token || (await getToken())
  const queryString = createQueryString({ scriptUuid, projectUuid })
  return requests.toJSON('DELETE', `storyboard/directors-vision?${queryString}`, {}, token)
}
